/**
 * Listado con scroll infinito: resultados de busqueda, categoria y linea.
 *
 * La primera pagina se pide como cualquier otra vista (`usePeticion`); las
 * siguientes se van sumando debajo con `cargarMas`, hasta llegar al total que
 * informo la API. Una categoria o una linea pueden empezar mas adelante
 * (`paginaInicial`, de `?pagina=`): es la URL por pagina que recorre Google, y
 * desde ahi el scroll sigue igual.
 *
 * Las paginas extra se guardan junto al listado al que pertenecen (`clave`:
 * termino o slug, y filtros): si el comprador cambia un filtro, lo acumulado
 * deja de contar sin tener que limpiarlo a mano, y una respuesta atrasada se
 * aborta.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import type { ListadoFiltrado, ProductoResumen } from '@elcarpintero/compartido';

import type { ErrorApi, ResultadoPeticion } from '../tipos/api';
import { aErrorApi } from '../utilidades/error-api';
import {
  paginasRecordadas,
  recordarPaginas,
  type PaginasSumadas,
} from '../utilidades/memoria-listados';
import { usePeticion } from './use-peticion';

/** Las paginas sumadas despues de la primera, que es la de `usePeticion`. */
type PaginasExtra = PaginasSumadas;

export interface ListadoInfinito<R extends ListadoFiltrado> extends ResultadoPeticion<R> {
  /** Si todavia faltan productos por traer para llegar al total. */
  quedanMas: boolean;
  /** La ultima pagina ya a la vista. */
  ultimaPagina: number;
  cargandoMas: boolean;
  /** Falla al pedir la pagina siguiente; lo ya cargado se sigue mostrando. */
  errorMas: ErrorApi | null;
  cargarMas: () => void;
}

const SIN_EXTRA: PaginasExtra = { clave: '', pagina: 0, productos: [] };

/** Suma sin repetir: si el catalogo cambio entre una pagina y otra, un producto puede correrse. */
function sumar(previos: ProductoResumen[], nuevos: ProductoResumen[]): ProductoResumen[] {
  const vistos = new Set(previos.map((producto) => producto.id));
  return [...previos, ...nuevos.filter((producto) => !vistos.has(producto.id))];
}

/**
 * @param clave identidad del listado (termino o slug, y filtros).
 * @param pedirPagina trae una pagina de ese listado.
 * @param paginaInicial la primera que se muestra (`?pagina=` de la URL).
 */
export function useListadoInfinito<R extends ListadoFiltrado>(
  claveListado: string,
  pedirPagina: (pagina: number, senal: AbortSignal) => Promise<R>,
  paginaInicial = 1,
): ListadoInfinito<R> {
  const clave = `${claveListado}|${paginaInicial}`;
  const primera = usePeticion((senal) => pedirPagina(paginaInicial, senal), [clave]);
  const [extra, setExtra] = useState<PaginasExtra>(SIN_EXTRA);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [errorMas, setErrorMas] = useState<ErrorApi | null>(null);
  const enCurso = useRef<AbortController | null>(null);
  // Entrada del historial: al volver atras, las paginas sumadas se recuperan
  // de la memoria (`memoria-listados.ts`) en vez de empezar otra vez de cero.
  const { key: entrada } = useLocation();

  // Otro listado: se corta la pagina que venia en camino y se limpian los avisos.
  useEffect(() => {
    setCargandoMas(false);
    setErrorMas(null);
    return () => {
      enCurso.current?.abort();
      enCurso.current = null;
    };
  }, [clave]);

  const vigente =
    extra.clave === clave ? extra : (paginasRecordadas(entrada, clave) ?? SIN_EXTRA);
  // Memorizado: las vistas recuerdan el ultimo listado por identidad (`useUltimoDe`).
  const datos = useMemo<R | null>(
    () =>
      primera.datos === null
        ? null
        : { ...primera.datos, productos: sumar(primera.datos.productos, vigente.productos) },
    [primera.datos, vigente.productos],
  );

  // La que devolvio la API, que acota una pagina pasada de la ultima.
  const ultimaPagina = vigente.pagina > 0 ? vigente.pagina : (primera.datos?.pagina ?? paginaInicial);
  const quedanMas =
    datos !== null && ultimaPagina < Math.ceil(datos.total / Math.max(1, datos.porPagina));

  const cargarMas = useCallback(() => {
    if (!quedanMas || enCurso.current !== null) return;

    const control = new AbortController();
    enCurso.current = control;
    const siguiente = ultimaPagina + 1;
    setCargandoMas(true);
    setErrorMas(null);

    pedirPagina(siguiente, control.signal)
      .then((respuesta) => {
        if (control.signal.aborted) return;
        const sumadas = {
          clave,
          pagina: respuesta.pagina,
          productos: sumar(vigente.productos, respuesta.productos),
        };
        setExtra(sumadas);
        recordarPaginas(entrada, sumadas);
      })
      .catch((error: unknown) => {
        if (control.signal.aborted) return;
        setErrorMas(aErrorApi(error));
      })
      .finally(() => {
        if (enCurso.current !== control) return;
        enCurso.current = null;
        setCargandoMas(false);
      });
    // `pedirPagina` se redefine en cada render; `clave` es su identidad.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quedanMas, clave, ultimaPagina, vigente.productos, entrada]);

  return { ...primera, datos, quedanMas, ultimaPagina, cargandoMas, errorMas, cargarMas };
}
